import React from "react";
import { ArrowRight, ShieldCheck, BadgeCheck, Truck, Award } from "lucide-react";
import { Button } from "../ui/button";
import { company } from "../../mock";
import { MoleculeField, Bubbles, AnimatedBeaker } from "./Chemistry";

// Unique chemistry-themed strip: periodic-table-style tiles for our 5 core products
const ELEMENTS = [
  { n: "01", sym: "HCl", name: "Hydrochloric" },
  { n: "02", sym: "H\u2082SO\u2084", name: "Sulphuric" },
  { n: "03", sym: "HNO\u2083", name: "Nitric" },
  { n: "04", sym: "CaCl\u2082", name: "Calcium Chl." },
  { n: "05", sym: "NaOCl", name: "Sod. Hypoch." }
];

const ElementStrip = () => (
  <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
    <div className="bg-slate-900/92 backdrop-blur rounded-2xl border border-white/10 shadow-xl shadow-slate-900/30 p-3 sm:p-3.5">
      <div className="flex items-center justify-between mb-2 sm:mb-2.5 px-1">
        <div className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-bold text-white">Our Core Chemistry</div>
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping" />
          <span className="text-[10px] sm:text-[10.5px] uppercase tracking-widest font-bold text-white/90">In Production</span>
        </div>
      </div>

      {/* Animated molecular bond line behind tiles */}
      <div className="relative">
        <svg className="absolute left-3 right-3 top-1/2 -translate-y-1/2 w-[calc(100%-24px)] h-6 pointer-events-none" viewBox="0 0 300 16" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0 8 Q 30 0 60 8 T 120 8 T 180 8 T 240 8 T 300 8" fill="none" stroke="#5eead4" strokeWidth="1" strokeDasharray="4 4" opacity="0.5">
            <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="2.4s" repeatCount="indefinite" />
          </path>
        </svg>

        <div className="relative grid grid-cols-5 gap-1.5 sm:gap-2">
          {ELEMENTS.map((e, i) => (
            <a
              key={e.sym}
              href="#products"
              className="elem-card group relative bg-white border border-slate-200 rounded-lg sm:rounded-xl p-1.5 sm:p-2 text-center hover:border-teal-500 hover:bg-teal-50/60"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="absolute top-0.5 left-1 sm:top-1 sm:left-1.5 text-[7.5px] sm:text-[8.5px] font-bold text-slate-400 group-hover:text-teal-700">{e.n}</div>
              <div className="font-display font-extrabold text-[12px] sm:text-[15px] leading-none text-slate-900 group-hover:text-teal-800 mt-2 sm:mt-2.5">{e.sym}</div>
              <div className="text-[7.5px] sm:text-[8.5px] font-semibold text-slate-500 mt-0.5 sm:mt-1 truncate">{e.name}</div>
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Hero = ({ onQuote }) => {
  return (
    <section id="home" className="relative overflow-hidden brand-soft-gradient">
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-cyan-200/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-teal-200/40 blur-3xl pointer-events-none" />

      {/* Floating chemistry molecules */}
      <MoleculeField density={6} />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-6 pt-10 pb-16 sm:pt-14 sm:pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 max-w-full px-3 py-1.5 rounded-full bg-white border border-teal-200/60 shadow-sm">
            <BadgeCheck className="h-4 w-4 text-teal-700 shrink-0" />
            <span className="text-[11.5px] sm:text-[12.5px] font-semibold tracking-wide text-slate-700 leading-snug">{company.certification} · GeM Registered · Since {company.established}</span>
          </div>

          <h1 className="mt-5 sm:mt-6 font-display font-extrabold text-slate-900 text-[32px] sm:text-[44px] md:text-[52px] lg:text-[62px] leading-[1.06]">
            India's trusted <span className="text-gradient-anim">Manufacturer, Supplier &amp; Repacker</span> of pure acids
          </h1>

          <p className="mt-5 sm:mt-6 text-slate-600 text-[15.5px] sm:text-[17px] leading-relaxed max-w-2xl">
            For over 27 years, {company.name} has <strong className="text-slate-800">manufactured, supplied and repacked</strong> Hydrochloric, Sulphuric and Nitric acids in <strong className="text-slate-800">C.P., L.R. and A.R. grades</strong> — together with Calcium Chloride and Sodium Hypochlorite — at our Vapi GIDC plant and supplied them directly to India's most demanding pharma, textile, steel and water-treatment industries. Factory-gate pricing. Pan-India dispatch in <strong className="text-slate-800">FRP / HDPE drums, IBCs and stainless tankers</strong>.
          </p>

          <div className="mt-7 sm:mt-8 flex flex-col xs:flex-row sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
            <Button onClick={onQuote} className="bg-teal-700 hover:bg-teal-800 text-white font-semibold h-12 px-6 rounded-lg text-[15px] shadow-md shadow-teal-900/20 w-full sm:w-auto">
              Request a Quotation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href="#products" className="inline-flex items-center justify-center h-12 px-6 rounded-lg border border-slate-300 bg-white hover:border-teal-600 hover:text-teal-700 font-semibold text-slate-800 text-[15px] transition-colors w-full sm:w-auto">
              Browse Products
            </a>
          </div>

          <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-x-7 gap-y-2 sm:gap-y-3 text-[12.5px] sm:text-[13.5px] text-slate-600">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-teal-700 shrink-0" /> ISO 9001:2015 batch testing</span>
            <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-teal-700 shrink-0" /> FRP / HDPE drums · IBCs · stainless tankers</span>
            <span className="inline-flex items-center gap-2"><Award className="h-4 w-4 text-teal-700 shrink-0" /> 500+ industrial clients</span>
          </div>
        </div>

        <div className="lg:col-span-5 fade-up">
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-cyan-100 to-teal-100 blur-2xl opacity-70" />
            <div className="relative rounded-3xl overflow-hidden border border-white shadow-2xl shadow-cyan-900/10">
              <img
                src="https://images.unsplash.com/photo-1629447388369-760612337eff?w=1200&q=80"
                alt="Manish Labchem chemical manufacturing plant at Vapi GIDC"
                className="w-full h-[340px] sm:h-[400px] lg:h-[460px] object-cover"
                loading="eager"
              />
              {/* Bubbles overlay over plant image */}
              <Bubbles count={10} />

              {/* Animated beaker badge - top-right (hidden on small screens to avoid clutter) */}
              <div className="hidden md:block absolute top-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-2 border border-slate-200 shadow-lg glow-pulse">
                <AnimatedBeaker fillPercent={68} />
              </div>

              {/* Unique periodic-table-style chemistry tiles */}
              <ElementStrip />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
